import {
  IRawSourceDescriptions,
  ISourceDescriptionsByServiceType,
  ISourceDescriptions,
  ISourceDescription,
  IRawSourceFieldInfos,
  ISourceFieldInfo,
  ISourceFieldInfos,
  ISourcesInfosPayload,
  IFeatureLayerInfo,
  INamespacedXyDbFieldInfos
} from '../../interfaces/models'
import {IUser, IFeatureLayerInfos} from '../../interfaces/reducers'
import {config} from '../../reducers/config'
import {FailedServiceInfo, DeniedServiceInfo, FeatureLayerInfo} from '../../models'
import {XyGisLayerInfosService, WFSLayerInfosService} from '..'
import {GeometryType} from '../../enums/GeometryType'
import {ViewSchemaType} from '../../enums/ViewSchemaType'
import {getVectorLayerStyle, requestImages} from '../../helpers'
import Axios from 'axios'
import {getMapService} from '../../reducers/map'
import appConfig from '../../../reducers/appConfig'

export class SourcesService {
  XyGisLayerInfosService: XyGisLayerInfosService
  WFSLayerInfosService: WFSLayerInfosService
  constructor(XyGisLayerInfosService: XyGisLayerInfosService, WFSLayerInfosService: WFSLayerInfosService) {
    this.XyGisLayerInfosService = XyGisLayerInfosService
    this.WFSLayerInfosService = WFSLayerInfosService
  }

  protected getSourcesByServiceType(
    rawSourceDescriptions: IRawSourceDescriptions,
    user?: IUser
  ): ISourceDescriptionsByServiceType {
    const sourceDescriptions = this.createSourceDescriptions(rawSourceDescriptions)
    return this.groupByServiceType(sourceDescriptions)
  }

  protected resolveInfoPayload(
    allInfos: (IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo)[],
    resolve: (value: ISourcesInfosPayload) => void,
    user?: IUser
  ) {
    const featureLayerInfos: IFeatureLayerInfos = {}
    const failedServices: {[key: string]: FailedServiceInfo} = {}
    const deniedServices: {[key: string]: DeniedServiceInfo} = {}
    allInfos.forEach(layerInfo => {
      if (layerInfo instanceof FailedServiceInfo) {
        failedServices[layerInfo.url] = layerInfo
      } else if (layerInfo instanceof DeniedServiceInfo) {
        deniedServices[layerInfo.url] = layerInfo
      } else if (layerInfo instanceof FeatureLayerInfo) {
        featureLayerInfos[layerInfo.url] = layerInfo
      }
    })
    this.applyNomenclatures(featureLayerInfos, user).then(result => {
      this.gatherLegendInfos(allInfos).then(() => {
        resolve({
          featureLayerInfos: result,
          failedServices,
          deniedServices
        })
      })
    })
  }

  public getInfosByXyDescriptions(
    rawSourceDescriptions: IRawSourceDescriptions,
    namespacedXyDbFieldInfos: INamespacedXyDbFieldInfos,
    user?: IUser
  ): Promise<ISourcesInfosPayload> {
    return new Promise((resolve, reject) => {
      const sourcesByServiceType = this.getSourcesByServiceType(rawSourceDescriptions, user)
      const allInfos = this.XyGisLayerInfosService.getFeatureDescriptions(
        sourcesByServiceType.xyGis,
        namespacedXyDbFieldInfos
      )
      this.resolveInfoPayload(allInfos, resolve, user)
    })
  }

  public getInfos(rawSourceDescriptions: IRawSourceDescriptions, user?: IUser): Promise<ISourcesInfosPayload> {
    return new Promise((resolve, reject) => {
      const sourcesByServiceType = this.getSourcesByServiceType(rawSourceDescriptions, user)
      this.getInfosAsync(sourcesByServiceType).then(allInfos => {
        this.resolveInfoPayload(allInfos, resolve, user)
      })
    })
  }

  public getInfosFromSources(sourceDescriptions: ISourceDescriptions, user?: IUser): Promise<ISourcesInfosPayload> {
    return new Promise((resolve, reject) => {
      const sourcesByServiceType = this.groupByServiceType(sourceDescriptions)
      this.getInfosAsync(sourcesByServiceType).then(allInfos => {
        this.resolveInfoPayload(allInfos, resolve, user)
      })
    })
  }

  public getConfigInfos(user?: IUser): Promise<ISourcesInfosPayload> {
    return new Promise((resolve, reject) => {
      this.getConfigRawSourceDescriptions(user)
        .then(rawSourceDescriptions => {
          const sourcesByServiceType = this.getSourcesByServiceType(rawSourceDescriptions, user)
          this.getInfosAsync(sourcesByServiceType).then(allInfos => {
            this.resolveInfoPayload(allInfos, resolve, user)
          })
        })
        .catch(error => {
          console.error(error)
          alert(`Error getting config infos at SourcesService:getConfigInfos`)
          reject(error)
        })
    })
  }

  protected gatherLegendInfos(layerInfosAll: (IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo)[]) {
    return new Promise((resolve, reject) => {
      const layerInfos: IFeatureLayerInfos = {}
      layerInfosAll.forEach(l => {
        if (l instanceof FeatureLayerInfo) {
          layerInfos[l.sourceDescription.id] = l
        }
      })
      requestImages(layerInfos)
        .then(() => {
          const wmsNamespaced: {[key: string]: string[]} = {}
          const vectorPromises: Promise<void>[] = []
          for (const i in layerInfos) {
            if (layerInfos[i].imageLegendRows.length == 0 && layerInfos[i].getGeometryType() != GeometryType.None) {
              if (
                layerInfos[i].hasVectorViewSchema() ||
                [
                  appConfig.reg.parcels.parcelsUrl,
                  appConfig.reg.quarters.quartersUrl,
                  appConfig.reg.regLine.id,
                  appConfig.reg.regOsLine.id,
                  appConfig.reg.regPoint.id,
                  appConfig.reg.streetreg.id
                ].indexOf(i) != -1
              ) {
                const mapService = getMapService()
                if (mapService) {
                  vectorPromises.push(
                    new Promise((res, rej) => {
                      this.XyGisLayerInfosService.renderVectorSymbology(
                        layerInfos[i],
                        getVectorLayerStyle(layerInfos[i], mapService)
                      )
                        .then(r => {
                          layerInfos[i].imageLegendRows = r
                          res()
                        })
                        .catch(e => {
                          console.error(e)
                          res()
                        })
                    })
                  )
                }
              } else if (layerInfos[i].hasImageViewSchema()) {
                if (
                  layerInfos[i].sourceDescription.serviceType == 'wfs' ||
                  (layerInfos[i].sourceDescription.serviceType == 'xyGis' && !layerInfos[i].sourceDescription.noLegend)
                ) {
                  if (!wmsNamespaced[layerInfos[i].sourceDescription.namespacedUrl]) {
                    wmsNamespaced[layerInfos[i].sourceDescription.namespacedUrl] = []
                  }
                  wmsNamespaced[layerInfos[i].sourceDescription.namespacedUrl].push(
                    layerInfos[i].sourceDescription.layerName
                  )
                }
              }
            }
          }

          const promises: Promise<void>[] = []

          function _arrayBufferToBase64(buffer: ArrayBuffer) {
            let binary = ''
            const bytes = new Uint8Array(buffer)
            const len = bytes.byteLength
            for (let i = 0; i < len; i++) {
              binary += String.fromCharCode(bytes[i])
            }
            return window.btoa(binary)
          }
          function getPngDimensions(base64: string) {
            const header = base64.slice(0, 50)
            const uint8 = Uint8Array.from(atob(header), c => c.charCodeAt(0))
            const dataView = new DataView(uint8.buffer, 0, 28)

            return {
              width: dataView.getInt32(16),
              height: dataView.getInt32(20)
            }
          }
          const LEGEND_CACHE_VERSION = 'v1'
          const LEGEND_CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 1 day
          const legendCacheKey = (ns: string, layer: string) => `legend:${LEGEND_CACHE_VERSION}:${ns}:${layer}`

          const applyLegendToLayerInfos = (
            namespaceUrl: string,
            layername: string,
            base64: string,
            width: number,
            height: number
          ) => {
            for (const i in layerInfos) {
              if (
                (layerInfos[i].sourceDescription.serviceType == 'wfs' ||
                  layerInfos[i].sourceDescription.serviceType == 'xyGis') &&
                layerInfos[i].sourceDescription.namespacedUrl == namespaceUrl &&
                layerInfos[i].sourceDescription.layerName == layername
              ) {
                layerInfos[i].imageLegendRows = [{base64, height, width, label: ''}]
              }
            }
          }

          for (const namespaceUrl in wmsNamespaced) {
            wmsNamespaced[namespaceUrl].forEach(layername => {
              // localStorage cache check — legends are static per layer URL.
              const cacheKey = legendCacheKey(namespaceUrl, layername)
              const cached = (() => {
                try {
                  const raw = localStorage.getItem(cacheKey)
                  if (!raw) return null
                  const parsed = JSON.parse(raw) as {base64: string; width: number; height: number; ts?: number}
                  if (!parsed.ts || Date.now() - parsed.ts > LEGEND_CACHE_TTL_MS) {
                    localStorage.removeItem(cacheKey)
                    return null
                  }
                  return parsed
                } catch {
                  return null
                }
              })()

              if (cached && cached.base64 && cached.width > 0 && cached.height > 0) {
                applyLegendToLayerInfos(namespaceUrl, layername, cached.base64, cached.width, cached.height)
                return
              }

              promises.push(
                new Promise(resolve => {
                  Axios.get(`${namespaceUrl}/wms?REQUEST=GetLegendGraphic&LAYER=${layername}&FORMAT=image%2Fpng`, {
                    responseType: 'arraybuffer'
                  })
                    .then(l => {
                      if (!l || !l.data || (typeof l.data == 'object' && l.data.byteLength == 0)) {
                        resolve()
                        return
                      }
                      const base64raw = _arrayBufferToBase64(l.data)
                      const base64 = 'data:image/png;base64,' + base64raw
                      const image = new Image()

                      image.onload = function () {
                        applyLegendToLayerInfos(namespaceUrl, layername, base64, image.width, image.height)
                        try {
                          localStorage.setItem(
                            cacheKey,
                            JSON.stringify({base64, width: image.width, height: image.height, ts: Date.now()})
                          )
                        } catch {
                          // localStorage full / disabled — fail silently, fetch will happen again next load.
                        }
                        resolve()
                      }

                      image.src = base64
                    })
                    .catch(e => {
                      console.error(e)
                      resolve()
                    })
                })
              )
            })
          }
          Promise.all(promises)
            .then(() => {
              Promise.all(vectorPromises)
                .then(() => {
                  resolve(undefined)
                })
                .catch(e => {
                  console.error(e)
                  resolve(undefined)
                })
            })
            .catch(e => {
              console.error(e)
              resolve(undefined)
            })
        })
        .catch(e => {
          console.error(e)
          resolve(undefined)
        })
    })
  }

  protected getInfosAsync(
    sourceDescriptionsByServiceType: ISourceDescriptionsByServiceType
  ): Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>> {
    return new Promise((resolve, reject) => {
      const descriptionPromises: Array<Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>>> = [
        this.WFSLayerInfosService.fetchFeaturesDescriptions(sourceDescriptionsByServiceType.wfs),
        this.XyGisLayerInfosService.fetchFeatureDescriptions(sourceDescriptionsByServiceType.xyGis)
      ]

      let result: Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo> = []
      Promise.all(descriptionPromises).then(descriptions => {
        descriptions.forEach(d => {
          result = result.concat(d)
        })
        resolve(result)
      })
    })
  }

  protected applyNomenclatures(infos: IFeatureLayerInfos, user?: IUser): Promise<IFeatureLayerInfos> {
    return new Promise((resolve, reject) => {
      resolve(infos)
    })
  }

  protected normalizeFiedInfos(rawSourceFieldInfos: IRawSourceFieldInfos, lang: string): ISourceFieldInfos {
    const infosCollection: {[key: string]: ISourceFieldInfo} = {}
    for (const key in rawSourceFieldInfos) {
      infosCollection[key] = {...rawSourceFieldInfos[key], label: rawSourceFieldInfos[key].label[lang]}
    }
    const sourceFieldInfos: ISourceFieldInfos = {...infosCollection}

    return sourceFieldInfos
  }

  protected getLocalizedProperty(prop: {[lang: string]: string}): string {
    let lang = config.getConfig().locale
    if (!prop[lang]) {
      lang = Object.keys(prop)[0]
    }
    return prop[lang]
  }

  protected localizeFieldInfos(fieldInfos: IRawSourceFieldInfos): ISourceFieldInfos {
    const newFieldInfos = {} as ISourceFieldInfos
    for (const f in fieldInfos) {
      newFieldInfos[f] = {...fieldInfos[f], label: this.getLocalizedProperty(fieldInfos[f].label)}
    }
    return newFieldInfos
  }

  protected getConfigRawSourceDescriptions(user?: IUser): Promise<IRawSourceDescriptions> {
    return new Promise((resolve, reject) => {
      resolve(config.getConfig().sourcesDescription)
    })
  }

  protected createSourceDescriptions(rawSourceDescriptions: IRawSourceDescriptions): ISourceDescriptions {
    const sourceDescriptions = {} as ISourceDescriptions
    for (const geoserverUrl in rawSourceDescriptions) {
      for (const namespace in rawSourceDescriptions[geoserverUrl].namespaces) {
        for (const groupName in rawSourceDescriptions[geoserverUrl].namespaces[namespace]) {
          const layers = rawSourceDescriptions[geoserverUrl].namespaces[namespace][groupName].layers
          const namespacedUrl = `${geoserverUrl}/${namespace}`
          for (const rawLayerName in layers) {
            const hashIndex = rawLayerName.lastIndexOf('#')
            let layerName = rawLayerName
            if (hashIndex != -1) {
              layerName = layerName.slice(0, hashIndex)
            }
            const id = `${geoserverUrl}/${namespace}/${rawLayerName}`
            const rawSourceDescription = layers[rawLayerName]
            let groupLabel = groupName
            const appConfig = config.getConfig()
            if (appConfig.groupAliases[appConfig.locale] && appConfig.groupAliases[appConfig.locale][groupName]) {
              groupLabel = appConfig.groupAliases[appConfig.locale][groupName]
            }
            const sourceDesc: ISourceDescription = {
              ...rawSourceDescription,
              constantSqlWhere: rawSourceDescription.constantSqlWhere || '1=1',
              refreshOnExtentChange: !!rawSourceDescription.refreshOnExtentChange,
              namespacedUrl,
              nameSpace: namespace,
              layerName,
              id: id,
              noRegister: rawSourceDescription.noRegister || false,
              url: `${geoserverUrl}/${namespace}/${layerName}`,
              readOnly: rawSourceDescription.readOnly || false,
              attachmentsReadOnly: rawSourceDescription.attachmentsReadOnly || false,
              renderMode: rawSourceDescription.renderMode || 'vector',
              z: rawSourceDescription.z || 1,
              identifyZ: rawSourceDescription.identifyZ || 1,
              noLegend: rawSourceDescription.noLegend || false,
              baseUrl: geoserverUrl,
              group: groupName,
              nonserver: !!rawSourceDescription.nonserver,
              serviceType: rawSourceDescriptions[geoserverUrl].serviceType,
              sourceTitle:
                rawSourceDescription.sourceTitle != undefined
                  ? rawSourceDescription.sourceTitle
                  : 'missing layer title',
              idField: rawSourceDescription.idField != undefined ? rawSourceDescription.idField : 'id',
              visible: rawSourceDescription.visible != undefined ? rawSourceDescription.visible : true,
              addOnMap: rawSourceDescription.addOnMap != undefined ? rawSourceDescription.addOnMap : true,
              opacity: rawSourceDescription.opacity != undefined ? rawSourceDescription.opacity : 1,
              imageViewType:
                rawSourceDescription.imageViewType != undefined
                  ? rawSourceDescription.imageViewType
                  : (() => {
                      // If pseudoViewSchema uses TileImage anywhere, route to tile (TileWMS).
                      // Image keeps the legacy single-image (ImageWMS) path so the kais
                      // cadastre layers don't lose their server-side label dedup.
                      const psv = rawSourceDescription.pseudoViewSchema || {}
                      for (const k in psv) {
                        if (psv[k] === ViewSchemaType.TileImage) return 'tile'
                      }
                      return 'image'
                    })(),
              hasAttachments:
                rawSourceDescription.hasAttachments != undefined ? rawSourceDescription.hasAttachments : false,
              doNotIdentify: !!rawSourceDescription.doNotIdentify,
              featureTitle:
                rawSourceDescription.featureTitle != undefined
                  ? this.getLocalizedProperty(rawSourceDescription.featureTitle)
                  : 'missing layer title',
              groupLabel: groupLabel,
              fieldInfos: this.localizeFieldInfos(rawSourceDescription.fieldInfos)
            }
            sourceDescriptions[id] = sourceDesc
          }
        }
      }
    }

    return sourceDescriptions
  }

  protected groupByServiceType(sourceDescriptions: ISourceDescriptions): ISourceDescriptionsByServiceType {
    const result: ISourceDescriptionsByServiceType = {
      xyGis: {},
      wfs: {}
    }
    for (const sourceUrl in sourceDescriptions) {
      const sourceDescription = sourceDescriptions[sourceUrl]
      if (sourceDescription.serviceType == 'wfs') {
        if (!result.wfs[sourceDescriptions[sourceUrl].namespacedUrl]) {
          result.wfs[sourceDescriptions[sourceUrl].namespacedUrl] = {}
        }
        result.wfs[sourceDescriptions[sourceUrl].namespacedUrl][sourceUrl] = sourceDescription
      } else if (sourceDescription.serviceType == 'xyGis') {
        if (!result.xyGis[sourceDescriptions[sourceUrl].namespacedUrl]) {
          result.xyGis[sourceDescriptions[sourceUrl].namespacedUrl] = {}
        }
        result.xyGis[sourceDescriptions[sourceUrl].namespacedUrl][sourceUrl] = sourceDescription
      } else {
        throw new Error(`${sourceDescription.serviceType} type not implemented`)
      }
    }

    return result
  }
}
